import type { FetchResult, InternalRefetchQueriesInclude } from '@apollo/client';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { EventPlaceType, useCreateEventPlaceMutation, useDeleteEventPlaceMutation, useDeletePointOnMapMutation, useUpdateEventPlaceMutation } from '@graphql/generated';
import { useState } from 'react';
import type { UpdateEventPlaceCacheArgs } from './useEventPlaceCacheUpdate';
import { useEventPlaceCacheUpdate } from './useEventPlaceCacheUpdate';

/**
 * Function to handle errors by logging them.
 * @param {unknown} error - The error to be logged.
 * @param {string} context - The context where the error occurred.
 * @param {string} message - A message to describe the error.
 */
const handleError = (error: unknown, context: string, message: string) => {
  addError(error, { context, message });
};

/**
 * Interface for creating an event place.
 */
interface CreateEventPlaceArg {
  payload: { googlePlaceId: string; pointOnMapId?: string | null };
  eventId?: string;
  refetchQueries?: ((result: FetchResult) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude;
}

/**
 * Interface for updating an event place.
 */
interface UpdateEventPlaceArg {
  payload: { id?: string | null; pointOnMapId?: string | null; googlePlaceId: string };
  refetchQueries?: ((result: FetchResult) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude;
}

/**
 * Interface for deleting an event place.
 */
interface DeleteEventPlaceArg {
  payload: { id?: string | null; pointOnMapId?: string | null };
  refetchQueries?: ((result: FetchResult) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude;
}

/**
 * Interface for event venue actions.
 */
interface EventPlaceActionsProps
  extends Readonly<{
    onCompleted: () => void;
    onError: () => void;
    updateWebsitePreview?: (updateEventPlaceCache?: UpdateEventPlaceCacheArgs | undefined) => void;
  }> {}

/**
 * Custom hook to handle event venue actions like create, update, and delete.
 * @param {EventPlaceActionsProps} actions - The callback functions for when the action is completed or an error occurs.
 * @returns {object} - The functions for creating, updating, and deleting event places, along with a loading state.
 */
export const useEventPlaceActions = ({ onCompleted, onError, updateWebsitePreview }: EventPlaceActionsProps) => {
  const { updateEventPlaceCache } = useEventPlaceCacheUpdate();
  const [updateEventPlaceCacheArgs, setUpdateEventPlaceCacheArgs] = useState<UpdateEventPlaceCacheArgs>();
  const { eventInfo, eventHandle } = useEventInfo();

  const handleOnCompleted = useEventCallback(() => {
    delete updateEventPlaceCacheArgs?.cache;
    updateWebsitePreview?.(updateEventPlaceCacheArgs);
    onCompleted();
  });

  const [deletePointOnMap, { loading: waitingForDeletePointOnMap }] = useDeletePointOnMapMutation({ onCompleted: handleOnCompleted, onError });
  const [createEventPlaceMutation, { loading: waitingForCreateEventPlace }] = useCreateEventPlaceMutation({ onCompleted: handleOnCompleted, onError });
  const [updateEventPlaceMutation, { loading: waitingForUpdateEventPlace }] = useUpdateEventPlaceMutation({ onCompleted: handleOnCompleted, onError });
  const [deleteEventPlaceMutation, { loading: waitingForDeleteEventPlace }] = useDeleteEventPlaceMutation({ onCompleted: handleOnCompleted, onError });

  /**
   * Creates an event place by calling the mutation.
   * @param {CreateEventPlaceArg} params - The parameters for creating the event place.
   */
  const createEventPlace = useEventCallback(async ({ payload, refetchQueries, eventId: eventIdParam }: CreateEventPlaceArg) => {
    const eventId = eventIdParam || eventInfo?.eventId;
    if (eventId) {
      await createEventPlaceMutation({
        update: (cache, result) => {
          if (result.data?.createEventPlace) {
            const updateEventPlaceCachePayload: UpdateEventPlaceCacheArgs = {
              cache,
              eventPlace: result.data.createEventPlace,
              eventHandle,
              action: 'add',
              pointOnMapId: payload.pointOnMapId,
            };
            setUpdateEventPlaceCacheArgs(updateEventPlaceCachePayload);
            updateEventPlaceCache(updateEventPlaceCachePayload);
          }
        },
        variables: { payload: { googlePlaceId: payload.googlePlaceId, eventId, type: EventPlaceType.venue } },
        refetchQueries,
      }).catch(error => handleError(error, 'Event Place', `Failed to createEventPlace - eventId: ${eventId}, googlePlaceId: ${payload.googlePlaceId}`));
    } else {
      handleError('Missing eventId', 'Event Place', `Failed to createEventPlace - googlePlaceId: ${payload.googlePlaceId}`);
    }
  });

  /**
   * Updates an event place by calling the mutation.
   * @param {UpdateEventPlaceArg} params - The parameters for updating the event place.
   */
  const updateEventPlace = useEventCallback(async ({ payload, refetchQueries }: UpdateEventPlaceArg) => {
    if (payload.id) {
      await updateEventPlaceMutation({
        update: (cache, result) => {
          if (result.data?.updateEventPlace) {
            const updateEventPlaceCachePayload: UpdateEventPlaceCacheArgs = { cache, eventPlace: result.data.updateEventPlace, eventHandle, action: 'update' };
            setUpdateEventPlaceCacheArgs(updateEventPlaceCachePayload);
            updateEventPlaceCache(updateEventPlaceCachePayload);
          }
        },
        variables: { payload: { id: payload.id, googlePlaceId: payload.googlePlaceId } },
        refetchQueries,
      }).catch(error => handleError(error, 'Event Place', `Failed to updateEventPlace - id: ${payload.id}, googlePlaceId: ${payload.googlePlaceId}`));
    } else if (payload.pointOnMapId && eventInfo?.eventId) {
      await deletePointOnMap({
        variables: { payload: { id: payload.pointOnMapId } },
        refetchQueries,
      })
        .then(() => createEventPlace({ payload: { googlePlaceId: payload.googlePlaceId, pointOnMapId: payload.pointOnMapId }, refetchQueries }))
        .catch(error => handleError(error, 'Event Place', `Failed to deletePointOnMap - pointOnMapId: ${payload.pointOnMapId}`));
    } else {
      handleError('Missing eventId', 'Event Place', `Failed to updateEventPlace - googlePlaceId: ${payload.googlePlaceId}`);
    }
  });

  /**
   * Deletes an event place by calling the mutation.
   * @param {DeleteEventPlaceArg} params - The parameters for deleting the event place.
   */
  const deleteEventPlace = useEventCallback(({ payload, refetchQueries }: DeleteEventPlaceArg): Promise<void> => {
    return new Promise(resolve => {
      if (payload.id) {
        deleteEventPlaceMutation({
          update: (cache, result) => {
            if (result.data?.deleteEventPlace) {
              const updateEventPlaceCachePayload: UpdateEventPlaceCacheArgs = { cache, eventPlace: result.data.deleteEventPlace, eventHandle, action: 'remove' };
              setUpdateEventPlaceCacheArgs(updateEventPlaceCachePayload);
              updateEventPlaceCache(updateEventPlaceCachePayload);
            }
          },
          variables: { deleteEventPlaceId: payload.id },
          refetchQueries,
        })
          .catch((error: unknown) => handleError(error, 'Event Place', `Failed to deleteEventPlace - id: ${payload.id}`))
          .finally(resolve);
      } else if (payload.pointOnMapId) {
        deletePointOnMap({
          update: (cache, result) => {
            if (result.data?.deletePointOnMap) {
              const updateEventPlaceCachePayload: UpdateEventPlaceCacheArgs = { cache, pointOnMapId: result.data.deletePointOnMap.id, eventHandle, action: 'remove' };
              setUpdateEventPlaceCacheArgs(updateEventPlaceCachePayload);
              updateEventPlaceCache(updateEventPlaceCachePayload);
            }
          },
          variables: { payload: { id: payload.pointOnMapId } },
          refetchQueries,
        })
          .catch(error => handleError(error, 'Event Place', `Failed to deletePointOnMap - pointOnMapId: ${payload.pointOnMapId}`))
          .finally(resolve);
      } else {
        handleError('Missing id', 'Event Place', 'Failed to deleteEventPlace');
        resolve();
      }
    });
  });

  const isLoading = waitingForDeletePointOnMap || waitingForCreateEventPlace || waitingForUpdateEventPlace || waitingForDeleteEventPlace;

  return { createEventPlace, updateEventPlace, deleteEventPlace, isLoading };
};
